import { format } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { connect } from 'react-redux';
import validator from 'validator';
import { postRequest } from '../../helpers/apiHandler';
import CustomAlert from '../CustomAlert';
import ReportIssueSuccess from './ReportIssueSuccess';
import CloseIcon from '../../layouts/SVG/CloseIcon';
import { GreenIssueIconSVG } from '../../layouts/SVG/GreenIconSVG';

const ReportIssue = ({ openReportIssue, onClose, access_token, user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loadingSend, setLoadingSend] = useState(false);
  const [successModalShow, setSuccessModalShow] = useState(false);
  const [priority, setPriority] = useState({ Low: false, Medium: false, High: false });
  const [severity, setSeverity] = useState({ Major: false, Minor: false, Critical: false });
  const [description, setDescription] = useState('');
  const [issueTime, setIssueTime] = useState('');
  const [priorityError, setPriorityError] = useState({ error: false, message: '' });
  const [severityError, setSeverityError] = useState({ error: false, message: '' });
  const [descriptionError, setDescriptionError] = useState({ error: false, message: '' });
  const [issueTimeError, setIssueTimeError] = useState({ error: false, message: '' });
  const [file, setFile] = useState(null);
  const [alertState, setAlertState] = useState({
    show: false,
    variant: 'danger',
    message: '',
  });
  const fileInputRef = useRef(null);

  const handleFileUpload = (e) => {
    const uploadedFile = e.target.files[0];
    setFile(uploadedFile);

    // Clear the file input
    e.target.value = null;
  };

  const handleFileClear = (e) => {
    setFile(null);
  };

  const handleCloseReportModal = () => {
    setIsOpen(false);
    onClose(false);
    setPriority({ Low: false, Medium: false, High: false });
    setSeverity({ Major: false, Minor: false, Critical: false });
    setDescription('');
    setIssueTime('');
  };

  const handleReportSubmit = (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    if (form.checkValidity() === false) {
      event.stopPropagation();
    }

    if (priority.Low === false && priority.Medium === false && priority.High === false) {
      return setPriorityError({
        error: true,
        message: 'Please select priority.',
      });
    }

    if (severity.Major === false && severity.Minor === false && severity.Critical === false) {
      return setSeverityError({
        error: true,
        message: 'Please select severity.',
      });
    }

    if (validator.isEmpty(issueTime)) {
      return setIssueTimeError({
        error: true,
        message: 'Please enter time of issue.',
      });
    }

    if (validator.isEmpty(description)) {
      return setDescriptionError({
        error: true,
        message: 'Please enter description of issue.',
      });
    }

    setLoadingSend(true);

    const formData = new FormData();
    formData.append(
      'priority',
      Object.keys(priority).find((key) => priority[key])
    );
    formData.append(
      'severity',
      Object.keys(severity).find((key) => severity[key])
    );
    formData.append('time_of_issue', issueTime);
    formData.append('description', description);
    if (file) {
      formData.append('attachment', true);
      formData.append('image', file);
    }

    postRequest('/v2/report-issue', formData, access_token.token, (res) => {
      if (res.success) {
        setSuccessModalShow(true);
        setIsOpen(false);
        setLoadingSend(false);
        handleCloseReportModal();
        // Clear the file so it doesn't double shown when the modal is opened again
        setFile(null);
      } else {
        setAlertState({
          show: true,
          variant: 'danger',
          message: res.message,
        });
        setLoadingSend(false);
      }
    });
  };

  const handleRadioChange = (target) => {
    const { name, value } = target;

    if (name === 'priority') {
      setPriority({ ...priority, [value]: true });
      setPriorityError({ error: false, message: '' });
    }
    if (name === 'severity') {
      setSeverity({ ...severity, [value]: true });
      setSeverityError({ error: false, message: '' });
    }
  };

  const handleDescriptionChange = (e) => {
    setDescription(e.target.value);
    setDescriptionError({ error: false, message: '' });
  };

  const handleTimeChange = (event) => {
    setIssueTime(event.target.value);
    setIssueTimeError({ error: false, message: '' });
  };

  useEffect(() => {
    setIsOpen(openReportIssue);
  }, [openReportIssue]);

  useEffect(() => {
    if (alertState.show) {
      setTimeout(() => {
        setAlertState({
          message: '',
          show: false,
          variant: '',
        });
      }, 10000);
    }
  }, [alertState]);

  return (
    <>
      <Modal centered className="modal-report-issue" show={isOpen} onHide={handleCloseReportModal}>
        <Modal.Body>
          <CustomAlert
            message={alertState.message}
            show={alertState.show}
            variant={alertState.variant}
            closeAlert={() => setAlertState({ show: false, variant: 'danger', message: '' })}
          />
          <CloseIcon handleModalClose={handleCloseReportModal} />
          <GreenIssueIconSVG />
          <Modal.Title as="h4" style={{ margin: '16px 0 24px' }}>
            Report an Issue
          </Modal.Title>
          <Form onSubmit={handleReportSubmit}>
            <Form.Group>
              <Form.Label>Priority</Form.Label>
              <div>
                <Form.Check
                  custom
                  inline
                  type="radio"
                  label="Low"
                  name="priority"
                  id="priority-1"
                  value="Low"
                  checked={priority.Low}
                  onChange={(evt) => handleRadioChange(evt.target)}
                  isInvalid={priorityError.error}
                />
                <Form.Check
                  custom
                  inline
                  type="radio"
                  label="Medium"
                  name="priority"
                  id="priority-2"
                  value="Medium"
                  checked={priority.Medium}
                  onChange={(evt) => handleRadioChange(evt.target)}
                  isInvalid={priorityError.error}
                />
                <Form.Check
                  custom
                  inline
                  type="radio"
                  label="High"
                  name="priority"
                  id="priority-3"
                  value="High"
                  checked={priority.High}
                  onChange={(evt) => handleRadioChange(evt.target)}
                  isInvalid={priorityError.error}
                />
              </div>
              <Form.Control.Feedback
                type="invalid"
                className={priorityError.error ? 'd-block' : 'd-none'}
              >
                {priorityError.message}
              </Form.Control.Feedback>
            </Form.Group>
            {/* Severity */}
            <Form.Group>
              <Form.Label>Severity</Form.Label>
              <div>
                <Form.Check
                  custom
                  inline
                  type="radio"
                  label="Minor"
                  name="severity"
                  id="severity-2"
                  value="Minor"
                  checked={severity.Minor}
                  onChange={(evt) => handleRadioChange(evt.target)}
                  isInvalid={severityError.error}
                />
                <Form.Check
                  custom
                  inline
                  type="radio"
                  label="Major"
                  name="severity"
                  id="severity-1"
                  value="Major"
                  checked={severity.Major}
                  onChange={(evt) => handleRadioChange(evt.target)}
                  isInvalid={severityError.error}
                />
                <Form.Check
                  custom
                  inline
                  type="radio"
                  label="Critical"
                  name="severity"
                  id="severity-3"
                  value="Critical"
                  checked={severity.Critical}
                  onChange={(evt) => handleRadioChange(evt.target)}
                  isInvalid={severityError.error}
                />
              </div>
              <Form.Control.Feedback
                type="invalid"
                className={severityError.error ? 'd-block' : 'd-none'}
              >
                {severityError.message}
              </Form.Control.Feedback>
            </Form.Group>
            {/* Time of issue */}
            <Form.Group>
              <Form.Label>Time of issue</Form.Label>
              <Form.Control
                type="datetime-local"
                value={issueTime}
                onChange={handleTimeChange}
                isInvalid={issueTimeError.error}
              />
              <Form.Control.Feedback type="invalid">{issueTimeError.message}</Form.Control.Feedback>
            </Form.Group>
            {/* Description */}
            <Form.Group>
              <Form.Label>Provide description of an issue or feedback</Form.Label>
              <Form.Control
                as="textarea"
                row="4"
                value={description}
                onChange={handleDescriptionChange}
                isInvalid={descriptionError.error}
              />
              <Form.Control.Feedback type="invalid">
                {descriptionError.message}
              </Form.Control.Feedback>
            </Form.Group>
            {/* Attachment */}
            <Form.Group>
              <Form.Label>Attachment</Form.Label>
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
                <Button
                  variant="ghost"
                  style={{ minWidth: 160 }}
                  onClick={() => {
                    if (fileInputRef.current) {
                      fileInputRef.current.click();
                    }
                  }}
                >
                  Upload
                </Button>
                {file && (
                  <div className="upload-info">
                    <CloseIcon handleModalClose={handleFileClear} />
                    {file.name}
                  </div>
                )}
              </div>
              {/* <Form.Control type="file" onChange={handleFileUpload} /> */}
            </Form.Group>
            {/* Success notice */}
            {issueTime !== '' && (
              <Form.Group className="notice-success">
                Hey <strong>{user.name}</strong>, For the issue/feedback on the{' '}
                <strong>Contact</strong> Page. Dated at{' '}
                <strong>{format(issueTime, 'dd MMM, HH:mm a')}</strong>. Our team will get in touch
                with you at <strong>{user.email}</strong>
              </Form.Group>
            )}
            {/* Submit button */}
            <Form.Group>
              <Button type="submit" variant="primary" style={{ minWidth: 160 }}>
                Send Report
              </Button>
            </Form.Group>
          </Form>
        </Modal.Body>
      </Modal>
      <ReportIssueSuccess
        successModalShow={successModalShow}
        setSuccessModalShow={setSuccessModalShow}
      />
    </>
  );
};

const mapStateToProps = (state) => {
  const { access_token, user } = state.auth;

  return {
    access_token,
    user,
  };
};

const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(ReportIssue);
